import React from 'react';
import { Building2, MapPin, Mail, Phone, FileText, ShieldCheck, Landmark, Hash, AtSign } from 'lucide-react';

/**
 * CorporateIdentityBlock — canonical corporate identity read-out.
 *
 * Reads the projection returned by GET /api/public/corporate-identity
 * (see backend/routes/corporate_identity_routes.py::_project_public_entity).
 * Fields the Owner has NOT registered (e.g. `phone`) are absent from the
 * response and must NOT be fabricated on the client. Every field below
 * is therefore rendered conditionally.
 *
 * Props:
 *   entity — { commercial_title, brand, tax_office, tax_id,
 *              trade_registry_number, mersis_number, chamber,
 *              chamber_registry_number, registered_address,
 *              general_email, kvkk_contact_email, kep_email, kep_status,
 *              phone, country }
 *   labels — resolved i18n strings
 *   variant — "full" (About bottom) | "summary" (About top card)
 */
const Row = ({ icon: Icon, label, value, testid, mono = false }) => (
  <div className="flex items-start gap-3 py-2" data-testid={testid}>
    <div className="mt-0.5 shrink-0 h-7 w-7 rounded-md bg-slate-100 flex items-center justify-center text-slate-600">
      <Icon className="h-4 w-4" aria-hidden />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-sm text-slate-900 break-words ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  </div>
);

const CorporateIdentityBlock = ({ entity, labels = {}, variant = 'full' }) => {
  if (!entity) return null;

  const {
    commercial_title, brand,
    tax_office, tax_id,
    trade_registry_number, mersis_number,
    chamber, chamber_registry_number,
    registered_address,
    general_email, kvkk_contact_email, kep_email, kep_status,
    phone,
  } = entity;

  if (variant === 'summary') {
    return (
      <div
        className="rounded-2xl border border-slate-200 bg-white/70 backdrop-blur px-6 py-5 sm:px-8 sm:py-6"
        data-testid="corporate-identity-summary"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="h-9 w-9 rounded-lg bg-[#3BA4C5]/10 flex items-center justify-center text-[#3BA4C5]">
            <ShieldCheck className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500">
              {labels.summaryEyebrow || 'A registered Türkiye health-tourism company'}
            </div>
            <div className="text-base sm:text-lg font-semibold text-slate-900" data-testid="corp-summary-title">
              {commercial_title || brand}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs sm:text-sm text-slate-700">
          {mersis_number && (
            <div data-testid="corp-summary-mersis">
              <span className="text-slate-500">{labels.mersis || 'MERSİS'}: </span>
              <span className="font-mono">{mersis_number}</span>
            </div>
          )}
          {tax_office && tax_id && (
            <div data-testid="corp-summary-tax">
              <span className="text-slate-500">{labels.taxOffice || 'Tax office'}: </span>
              <span>{tax_office} · </span>
              <span className="font-mono">{tax_id}</span>
            </div>
          )}
          {chamber && chamber_registry_number && (
            <div data-testid="corp-summary-chamber">
              <span className="text-slate-500">{labels.chamber || 'Chamber'}: </span>
              <span>{chamber} · </span>
              <span className="font-mono">{chamber_registry_number}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <section
      id="corporate-identity-block"
      className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-10"
      data-testid="corporate-identity-block"
    >
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-900" data-testid="corp-block-title">
          {labels.blockHeading || 'Corporate Identity'}
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          {labels.blockSubheading
            || 'Public registration details of the entity operating this platform.'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
        {commercial_title && (
          <Row
            icon={Building2}
            label={labels.commercialTitle || 'Commercial title'}
            value={commercial_title}
            testid="corp-row-commercial-title"
          />
        )}
        {brand && brand !== commercial_title && (
          <Row
            icon={ShieldCheck}
            label={labels.brand || 'Brand'}
            value={brand}
            testid="corp-row-brand"
          />
        )}
        {mersis_number && (
          <Row
            icon={Hash}
            label={labels.mersis || 'MERSİS number'}
            value={mersis_number}
            testid="corp-row-mersis"
            mono
          />
        )}
        {trade_registry_number && (
          <Row
            icon={FileText}
            label={labels.tradeRegistry || 'Trade registry no.'}
            value={trade_registry_number}
            testid="corp-row-trade-registry"
            mono
          />
        )}
        {tax_office && (
          <Row
            icon={Landmark}
            label={labels.taxOffice || 'Tax office'}
            value={tax_office}
            testid="corp-row-tax-office"
          />
        )}
        {tax_id && (
          <Row
            icon={Hash}
            label={labels.taxId || 'Tax ID'}
            value={tax_id}
            testid="corp-row-tax-id"
            mono
          />
        )}
        {chamber && (
          <Row
            icon={Landmark}
            label={labels.chamber || 'Chamber'}
            value={chamber}
            testid="corp-row-chamber"
          />
        )}
        {chamber_registry_number && (
          <Row
            icon={Hash}
            label={labels.chamberRegistry || 'Chamber registry no.'}
            value={chamber_registry_number}
            testid="corp-row-chamber-registry"
            mono
          />
        )}
        {registered_address && (
          <div className="md:col-span-2">
            <Row
              icon={MapPin}
              label={labels.registeredAddress || 'Registered address'}
              value={registered_address}
              testid="corp-row-address"
            />
          </div>
        )}
        {general_email && (
          <Row
            icon={Mail}
            label={labels.generalEmail || 'General enquiries'}
            value={
              <a href={`mailto:${general_email}`} className="text-[#3BA4C5] hover:underline">
                {general_email}
              </a>
            }
            testid="corp-row-general-email"
          />
        )}
        {kvkk_contact_email && (
          <Row
            icon={Mail}
            label={labels.kvkkEmail || 'Data protection (KVKK) contact'}
            value={
              <a href={`mailto:${kvkk_contact_email}`} className="text-[#3BA4C5] hover:underline">
                {kvkk_contact_email}
              </a>
            }
            testid="corp-row-kvkk-email"
          />
        )}
        {kep_email ? (
          <Row
            icon={AtSign}
            label={labels.kep || 'Registered Electronic Mail (KEP)'}
            value={kep_email}
            testid="corp-row-kep"
          />
        ) : kep_status === 'application_in_progress' ? (
          <Row
            icon={AtSign}
            label={labels.kep || 'Registered Electronic Mail (KEP)'}
            value={
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 text-xs font-medium">
                {labels.applicationInProgress || 'Application in progress'}
              </span>
            }
            testid="corp-row-kep-pending"
          />
        ) : null}
        {phone && (
          <Row
            icon={Phone}
            label={labels.phone || 'Phone'}
            value={phone}
            testid="corp-row-phone"
          />
        )}
      </div>
    </section>
  );
};

export default CorporateIdentityBlock;
